// React & External Libraries
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowUp, Linkedin, Facebook, Instagram, Phone, Mail } from "lucide-react";

// Assets
import georgianFlag from "@/assets/flag-georgia.png";
import euFlag from "@/assets/flag-europe.png";

// Internal UI Components
import { Button } from "@/components/ui/button";


// Feature Components
import { AuthModal } from "@/components/AuthModal";
import { LandingNav } from "@/components/landing/LandingNav";
import { PainRecognitionSection } from "@/components/landing/PainRecognitionSection";
import { LinearFlowSection } from "@/components/landing/LinearFlowSection";
import { DemoTodayBoard } from "@/components/landing/DemoTodayBoard";
import { DemoCourierFlow } from "@/components/landing/DemoCourierFlow";
import { DemoPlanningRadar } from "@/components/landing/DemoPlanningRadar";
import { DemoOrderIntake } from "@/components/landing/DemoOrderIntake";
import { SharedLogSection } from "@/components/landing/SharedLogSection";
import { WhoItsForSection } from "@/components/landing/WhoItsForSection";
import { FinalCTASection } from "@/components/landing/FinalCTASection";
import { PricingSection } from "@/components/landing/PricingSection";

// Contexts & Hooks
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuthContext } from "@/contexts/AuthContext";

// Integrations & Constants
import { LANDING_LAYOUT, VALID_REDIRECT_PATHS } from "@/lib/landingConstants";

export default function Landing() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isAuthenticated, session } = useAuthContext();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [redirectPath, setRedirectPath] = useState("/orders");
  
  const [isProcessingReset, setIsProcessingReset] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - LANDING_LAYOUT.NAV_HEIGHT;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Handle URL params on mount (register redirect, auth modal trigger, reset action)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldShowRegister = params.get('register') === 'true';
    const shouldShowAuth = params.get('auth') === 'true';
    const isResetAction = params.get('action') === 'reset_password';
    
    if (shouldShowRegister) {
      navigate("/register");
      return;
    }
    
    if (shouldShowAuth) {
      const nextPath = params.get('next');
      setAuthModalOpen(true);
      if (nextPath && VALID_REDIRECT_PATHS.includes(nextPath as any)) {
        setRedirectPath(nextPath);
      }
      window.history.replaceState({}, '', '/');
      return;
    }
    
    if (isResetAction && !isAuthenticated) {
      setIsProcessingReset(true);
    }
  }, [navigate, isAuthenticated]);

  // Reactive redirect: when user becomes authenticated, navigate away
  useEffect(() => {
    if (!isAuthenticated || !session) return;
    
    const params = new URLSearchParams(window.location.search);
    const forceStay = params.get('stay') === 'true';
    if (forceStay) return;

    const needsPasswordChange = session.user?.user_metadata?.needs_password_change;
    
    if (needsPasswordChange) {
      navigate("/reset-password");
    } else {
      navigate(redirectPath);
    }
  }, [isAuthenticated, session, navigate, redirectPath]);


  // Defer Chatway script loading
  useEffect(() => {
    const timer = setTimeout(() => {
      const script = document.createElement("script");
      script.id = "chatway";
      script.async = true;
      script.src = "https://cdn.chatway.app/widget.js?id=AfNBgLnLZKPl";
      document.head.appendChild(script);
    }, 2000);

    return () => {
      clearTimeout(timer);
      const existingScript = document.getElementById("chatway");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);


  // Back to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Loading state during password reset
  if (isProcessingReset) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">{t("auth.loading") || "Setting up your account..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* Navigation - Reusable Landing Nav */}
      <LandingNav
        actions={
          <button
            onClick={() => setAuthModalOpen(true)}
            className="text-white/80 hover:text-white text-sm transition-colors"
          >
            {t("landing.signIn")}
          </button>
        }
      />

      <main>
        {/* HERO - Left-aligned on desktop, Georgian Utility approach */}
        <section className="bg-background min-h-[55vh] sm:min-h-[50vh] flex items-center py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 w-full">
            <div className="max-w-2xl md:text-left text-center space-y-6">
              {/* Local identity - language-aware */}
              <div className="flex items-center gap-3 md:justify-start justify-center">
                {language === "ka" && (
                  <img src={georgianFlag} alt="" className="w-6 h-4 rounded-sm" />
                )}
                <span className="text-sm font-medium text-foreground tracking-wide">
                  {t("landing.localTrust.builtFor")}
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight tracking-tight">
                {t("landing.hero.headline")}
              </h1>
              <p className="text-muted-foreground text-lg sm:text-xl max-w-lg">
                {t("landing.hero.subheadline")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:justify-start justify-center pt-2">
                <Button
                  size="lg"
                  onClick={() => navigate("/explore")}
                  className="min-h-[48px] px-8 text-base"
                >
                  {t("landing.hero.cta")}
                </Button>
              </div>
              {/* Quiet trial info below CTA */}
              <p className="text-xs text-muted-foreground md:text-left text-center">
                {t("landing.ctaSubtext")}
              </p>
            </div>
          </div>
        </section>

        {/* PAIN RECOGNITION - Stacked cards */}
        <PainRecognitionSection />

        {/* LINEAR FLOW - Simplified pipeline */}
        <LinearFlowSection />

        {/* DEMO SECTION 1: Today Board - Primary Visual Proof (Full-width) */}
        <section className="bg-muted py-12 sm:py-14">
          <div className="max-w-5xl mx-auto px-4">
            {/* Header with explanation */}
            <div className="mb-8 max-w-2xl">
              <p className="text-xs font-bold text-primary uppercase tracking-widest mb-2">
                {t("landing.preview.label")}
              </p>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight mb-3">
                {t("landing.preview.boardHeadline")}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {t("landing.preview.boardDescription")}
              </p>
            </div>
            
            {/* Demo component - full width */}
            <DemoTodayBoard />
          </div>
        </section>
        
        {/* DEMO SECTION 2: Courier Flow - Manager to Courier Handoff (Full-width) */}
        <section className="bg-background py-12 sm:py-14">
          <div className="max-w-5xl mx-auto px-4">
            {/* Header with explanation */}
            <div className="mb-8 max-w-2xl">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight mb-3">
                {t("landing.preview.courierHeadline")}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {t("landing.preview.courierDescription")}
              </p>
            </div>
            
            {/* Demo component - full width */}
            <DemoCourierFlow />
          </div>
        </section>

        {/* PLANNING RADAR - Calm foresight, not urgency */}
        <section className="bg-muted py-10 sm:py-12">
          <div className="max-w-xl mx-auto px-4">
            <div className="text-center mb-4">
              <h3 className="text-base font-medium text-foreground">
                {t("landing.preview.planningHeadline")}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t("landing.preview.planningDescription")}
              </p>
            </div>
            <div className="flex justify-center">
              <DemoPlanningRadar />
            </div>
          </div>
        </section>

        {/* SHARED LOG - Key Differentiator with realistic demo */}
        <SharedLogSection />

        {/* SHAREABLE ORDER INTAKE - External chaos control */}
        <section className="bg-muted py-10 sm:py-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="mb-6 max-w-xl md:text-left text-center md:mx-0 mx-auto">
              <h2 className="text-xl font-bold text-foreground mb-2">
                {t("landing.preview.intakeHeadline")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t("landing.preview.intakeDescription")}
              </p>
            </div>
            <DemoOrderIntake />
          </div>
        </section>

        {/* WHO IT'S FOR - Plain text */}
        <WhoItsForSection />

        {/* ABOUT US - Comprehensive Company Profile */}
        <section id="about" className="bg-background py-10 sm:py-12">
          <div className="max-w-3xl mx-auto px-4 space-y-8">
            
            {/* What We Do */}
            <div className="border-accent-left pl-6 space-y-2">
              <h3 className="text-sm font-bold text-primary uppercase tracking-widest">
                {t("landing.aboutUs.whatWeDoLabel")}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                {t("landing.aboutUs.whatWeDoText")}
              </p>
            </div>
            
            {/* Who It's For */}
            <div className="border-accent-left pl-6 space-y-2">
              <h3 className="text-sm font-bold text-foreground uppercase tracking-widest">
                {t("landing.aboutUs.whoItsForLabel")}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                {t("landing.aboutUs.whoItsForText")}
              </p>
            </div>
            
            {/* Product Stage */}
            <div className="border-accent-left pl-6 space-y-2">
              <h3 className="text-sm font-bold text-foreground uppercase tracking-widest">
                {t("landing.aboutUs.stageLabel")}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                {t("landing.aboutUs.stageText")}
              </p>
            </div>
            
            {/* Team */}
            <div className="border-accent-left pl-6 space-y-3">
              <h3 className="text-sm font-bold text-foreground uppercase tracking-widest">
                {t("landing.aboutUs.teamLabel")}
              </h3>
              <div className="space-y-2 text-muted-foreground text-sm sm:text-base leading-relaxed">
                <p>
                  {t("landing.aboutUs.teamFounder")}
                  <a 
                    href="https://www.linkedin.com/in/jaba-tsenteradze/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 ml-2 text-primary hover:underline underline"
                  >
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                  </a>
                </p>
                <p>{t("landing.aboutUs.teamMembers")}</p>
              </div>
            </div>
            
            {/* Closing Statement */}
            <div className="pt-4 border-t border-border/30">
              <p className="text-muted-foreground text-sm italic leading-relaxed">
                {t("landing.aboutUs.closingText")}
              </p>
            </div>
            
          </div>
        </section>

        {/* PRICING - 3-Tier M3 Cards */}
        <PricingSection />

        {/* FINAL CTA - Calm invitation */}
        <FinalCTASection onExploreClick={() => navigate("/explore")} />
      </main>

      {/* Footer - Two Column Layout */}
      <footer className="bg-background border-t border-border/50 py-8 sm:py-10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-8">
            
            {/* Left Column - Contact & Social */}
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a href="tel:+995505051685" className="flex items-center gap-2 hover:text-foreground transition-colors w-fit">
                  <Phone className="h-4 w-4" />
                  <span>+995 505 051 685</span>
                </a>
                <a href="mailto:info@orderflow.ge" className="flex items-center gap-2 hover:text-foreground transition-colors w-fit">
                  <Mail className="h-4 w-4" />
                  <span>info@orderflow.ge</span>
                </a>
              </div>
              
              {/* Social Media Links */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.facebook.com/profile.php?id=61583496086690" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-muted-foreground hover:text-primary transition-colors" 
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.instagram.com/orderflow.ge" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors" 
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/company/orderflowge" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors" 
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
            
            {/* Right Column - Legal & Info */}
            <div className="flex flex-col sm:items-end gap-3 text-sm">
              {/* Georgian Flag Line */}
              <p className="flex items-center gap-2 text-muted-foreground">
                {t("landing.georgianSupport")}
                <img src={georgianFlag} alt="Georgian flag" className="h-4 w-auto rounded-sm" />
                <img src={euFlag} alt="European Union flag" className="h-4 w-auto rounded-sm" />
              </p>
              
              {/* Legal Links */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
                  {t("register.privacyPolicy")}
                </Link>
                <span className="text-border/50">•</span>
                <Link to="/terms-of-service" className="hover:text-foreground transition-colors">
                  {t("register.termsOfService")}
                </Link>
              </div>
              
              {/* Copyright */}
              <p className="text-xs text-muted-foreground">
                © 2025 OrderFlow
              </p>
            </div>
            
          </div>
        </div>
      </footer>

      {/* Modals */}
      {authModalOpen && (
        <AuthModal 
          open={authModalOpen} 
          onOpenChange={setAuthModalOpen}
          redirectPath={redirectPath}
          onSwitchToRegister={() => {
            setAuthModalOpen(false);
            navigate("/register");
          }}
        />
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          size="icon"
          className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full shadow-lg animate-fade-in hover:scale-110 transition-transform"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
}
